// HomeDashboard.js — Persona-Based Home Dashboard (Full Capability Map)
// All 42 capabilities organized into tiered persona journeys
import React, { useState, useEffect } from 'react';
import {
  TrendingUp, Building2, Target, Wallet, Star, GraduationCap,
  Settings, AlertTriangle, GitCompare, Activity, Zap, Search,
  BarChart2, ArrowRight, Sparkles, BookOpen, Shield,
  Calculator, Building, UserCheck, Briefcase, Scale, Globe,
  AlertCircle, Layers, Box, Calendar, BarChart3, Bell, Rocket,
  Trophy, Lock, ChevronRight, Brain, Check,
  Crown,
} from 'lucide-react';
import PageAwareAI from './PageAwareAI';
import RunningTicker from './RunningTicker';
import MarketMovers from './MarketMovers';

// ─── Tier Config ──────────────────────────────────────────────────────────────
const TIERS = {
  free:        { label: 'Free',        color: '#60a5fa', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.3)',  icon: '🆓' },
  pro:         { label: 'Pro',         color: '#a78bfa', bg: 'rgba(139,92,246,0.12)',  border: 'rgba(139,92,246,0.3)',  icon: '⚡' },
  premium:     { label: 'Premium',     color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.3)',  icon: '👑' },
  options_pro: { label: 'Options Pro', color: '#f97316', bg: 'rgba(249,115,22,0.12)',  border: 'rgba(249,115,22,0.3)',  icon: '🎯' },
};

// ─── Complete Capability Registry ─────────────────────────────────────────────
const CAPABILITIES = {
  // FREE
  market:              { id: 'market',             title: 'Market Overview',        icon: TrendingUp,    tier: 'free',        cat: 'Markets',   desc: 'Live indices, FRED data, macro indicators' },
  company:             { id: 'company',             title: 'Company Analysis',       icon: Building2,     tier: 'free',        cat: 'Research',  desc: 'Financials, earnings, news + AI insights' },
  compare:             { id: 'compare',             title: 'Stock Comparison',       icon: GitCompare,    tier: 'free',        cat: 'Research',  desc: 'Side-by-side multi-stock metrics' },
  watchlist:           { id: 'watchlist',           title: 'Watchlist',              icon: Star,          tier: 'free',        cat: 'Portfolio', desc: 'Monitor stocks of interest' },
  portfolio:           { id: 'portfolio',           title: 'My Portfolio',           icon: Wallet,        tier: 'free',        cat: 'Portfolio', desc: 'Track holdings, P&L, allocation' },
  'paper-trading':     { id: 'paper-trading',       title: 'Paper Trading',          icon: Target,        tier: 'free',        cat: 'Portfolio', desc: '$100K virtual portfolio, risk-free' },
  education:           { id: 'education',           title: 'Education Center',       icon: GraduationCap, tier: 'free',        cat: 'Learn',     desc: 'Investing concepts and strategies' },
  learn:               { id: 'learn',               title: 'Learn Academy',          icon: BookOpen,      tier: 'free',        cat: 'Learn',     desc: 'Structured courses and lessons' },
  'technical-analysis':{ id: 'technical-analysis',  title: 'Technical Analysis',     icon: Activity,      tier: 'free',        cat: 'Research',  desc: '50+ indicators with AI-powered signals' },
  'ipo-calendar':      { id: 'ipo-calendar',        title: 'IPO Calendar',           icon: Calendar,      tier: 'free',        cat: 'Markets',   desc: 'Upcoming & recent public offerings' },
  settings:            { id: 'settings',            title: 'Settings & Alerts',      icon: Settings,      tier: 'free',        cat: 'Account',   desc: 'Preferences, notifications, account' },
  'sports-market':     { id: 'sports-market',       title: 'Public Sports Market',   icon: Trophy,        tier: 'free',        cat: 'Sports',    desc: 'Trade MANU, DKNG, TKO, NKE & 16 more' },
  'sports-org-portal': { id: 'sports-org-portal',   title: 'Org Application Portal', icon: Building,      tier: 'free',        cat: 'Sports',    desc: 'Franchise Tier B certification portal' },
  trading:             { id: 'trading',             title: 'Live Trading',           icon: Rocket,        tier: 'free',        cat: 'Portfolio', desc: 'Broker-dealer partnership in progress' },
  subscription:        { id: 'subscription',        title: 'Upgrade Plan',           icon: Crown,         tier: 'free',        cat: 'Account',   desc: 'Pro, Premium & Options Pro subscriptions' },
  // PRO
  recession:           { id: 'recession',           title: 'Recession Dashboard',    icon: AlertTriangle, tier: 'pro',         cat: 'Markets',   desc: '7-indicator model, yield curve analysis' },
  'etf-xray':          { id: 'etf-xray',            title: 'ETF X-Ray',              icon: Search,        tier: 'pro',         cat: 'ETF',       desc: "See exactly what's inside any ETF" },
  'etf-compare':       { id: 'etf-compare',         title: 'ETF Comparison',         icon: BarChart2,     tier: 'pro',         cat: 'ETF',       desc: 'Side-by-side ETF performance analysis' },
  crypto:              { id: 'crypto',              title: 'Crypto Deep Dive',       icon: TrendingUp,    tier: 'pro',         cat: 'Crypto',    desc: 'Bitcoin, Ethereum, top 20 cryptos' },
  'dca-calculator':    { id: 'dca-calculator',      title: 'DCA Calculator',         icon: Calculator,    tier: 'pro',         cat: 'Portfolio', desc: 'Simulate dollar-cost averaging returns' },
  // PREMIUM
  forex:               { id: 'forex',               title: 'Forex Dashboard',        icon: Globe,         tier: 'premium',     cat: 'Markets',   desc: 'Currency pairs, rates & converter' },
  rebalancer:          { id: 'rebalancer',          title: 'Portfolio Rebalancer',   icon: Scale,         tier: 'premium',     cat: 'Portfolio', desc: 'Target vs actual allocation + suggestions' },
  'crypto-correlation':{ id: 'crypto-correlation',  title: 'Crypto Correlation',     icon: BarChart2,     tier: 'premium',     cat: 'Crypto',    desc: 'Cross-asset correlation matrix' },
  'anomaly-detector':  { id: 'anomaly-detector',    title: 'Anomaly Detector',       icon: AlertCircle,   tier: 'premium',     cat: 'Research',  desc: 'Unusual volume, price gaps, RSI extremes' },
  'insider-trading':   { id: 'insider-trading',     title: 'Insider Trading',        icon: UserCheck,     tier: 'premium',     cat: 'Smart $',   desc: 'Track CEO & director buy/sell signals' },
  'congress-trades':   { id: 'congress-trades',     title: 'Congress Trades',        icon: Building,      tier: 'premium',     cat: 'Smart $',   desc: 'Track what politicians are trading' },
  'whale-watcher':     { id: 'whale-watcher',       title: 'Whale Watcher',          icon: Briefcase,     tier: 'premium',     cat: 'Smart $',   desc: 'Buffett, Dalio, Ackman & top funds' },
  'sports-private':    { id: 'sports-private',      title: 'Private Sports Market',  icon: Lock,          tier: 'premium',     cat: 'Sports',    desc: 'Tier A/B/C · shadow tokens · deal rooms' },
  'top-winners':       { id: 'top-winners',         title: 'Top 10 Winners',         icon: Trophy,        tier: 'free',        cat: 'AI Picks',  desc: 'AI multi-signal stock picks · live ranked' },
  // OPTIONS PRO
  options:             { id: 'options',             title: 'Options Chain',          icon: Activity,      tier: 'options_pro', cat: 'Options',   desc: 'Full chain with Greeks and IV analysis' },
  'iv-crush':          { id: 'iv-crush',            title: 'IV Crush Predictor',     icon: Zap,           tier: 'options_pro', cat: 'Options',   desc: 'Post-earnings volatility collapse analysis' },
  'gamma-exposure':    { id: 'gamma-exposure',      title: 'Gamma Exposure (GEX)',   icon: Layers,        tier: 'options_pro', cat: 'Options',   desc: 'Market maker positioning & key levels' },
  'volatility-surface':{ id: 'volatility-surface',  title: '3D Volatility Surface',  icon: Box,           tier: 'options_pro', cat: 'Options',   desc: 'Interactive IV surface visualization' },
  'opex-calendar':     { id: 'opex-calendar',       title: 'OpEx Calendar',          icon: Calendar,      tier: 'options_pro', cat: 'Options',   desc: 'Expiration dates with GEX predictions' },
  'gex-history':       { id: 'gex-history',         title: 'Historical GEX',         icon: BarChart3,     tier: 'options_pro', cat: 'Options',   desc: 'GEX changes and patterns over time' },
  'gex-alerts':        { id: 'gex-alerts',          title: 'GEX Alerts',             icon: Bell,          tier: 'options_pro', cat: 'Options',   desc: 'Real-time gamma level monitoring' },
};

// ─── Persona Definitions — Full Tiered Journeys ───────────────────────────────
const PERSONAS = [
  {
    id: 'student',
    label: 'Finance Student',
    emoji: '🎓',
    tagline: 'Learn by doing with real data',
    description: 'Studying finance, economics, or business. Use live market data to apply classroom concepts, build analysis skills, and practice safely.',
    accentColor: '#38bdf8',
    gradientFrom: '#0c1d2e',
    gradientTo: '#0c4a6e',
    journeyTitle: 'Student Learning Path',
    tiers: [
      {
        tier: 'free',
        label: 'Start Here — Free',
        subtitle: 'All the tools you need to learn and practice',
        tools: [
          { id: 'learn',               why: 'Structured courses on investing & markets' },
          { id: 'market',              why: 'Apply macroeconomics theory to live FRED data' },
          { id: 'company',             why: 'Run financial statement analysis on real companies' },
          { id: 'compare',             why: 'Compare companies like a case study' },
          { id: 'technical-analysis',  why: 'See RSI, MACD, Bollinger Bands in action' },
          { id: 'paper-trading',       why: 'Test investment theses with zero risk' },
          { id: 'watchlist',           why: 'Track stocks you are studying for class' },
          { id: 'ipo-calendar',        why: 'Study IPO valuation & market timing' },
          { id: 'trading',             why: 'Broker-dealer partnership in progress' },
        ],
      },
      {
        tier: 'pro',
        label: 'Pro — $9.99/mo',
        subtitle: 'Go deeper with macro & modeling tools',
        tools: [
          { id: 'recession',       why: 'Study yield curves & leading indicators live' },
          { id: 'etf-xray',        why: 'Deconstruct index fund holdings for portfolio theory' },
          { id: 'etf-compare',     why: 'Compare factor exposures across ETFs' },
          { id: 'dca-calculator',  why: 'Model DCA vs lump-sum strategies for coursework' },
          { id: 'crypto',          why: 'Analyze digital asset markets and on-chain data' },
        ],
      },
      {
        tier: 'premium',
        label: 'Premium — $19.99/mo',
        subtitle: 'Institutional-grade research for advanced study',
        tools: [
          { id: 'forex',              why: 'Apply FX theory — PPP, carry trade, cross rates' },
          { id: 'insider-trading',    why: 'Study information asymmetry & SEC Form 4 filings' },
          { id: 'congress-trades',    why: 'Research political economy & market signals' },
          { id: 'anomaly-detector',   why: 'Detect market inefficiencies for research projects' },
          { id: 'rebalancer',         why: 'Model modern portfolio theory rebalancing live' },
          { id: 'crypto-correlation', why: 'Study cross-asset correlations & diversification' },
        ],
      },
    ],
  },
  {
    id: 'beginner',
    label: 'New Investor',
    emoji: '🌱',
    tagline: 'Build confidence before risking real money',
    description: 'Just starting out. Want to understand markets, research stocks, and build good habits before investing real money.',
    accentColor: '#22c55e',
    gradientFrom: '#052e16',
    gradientTo: '#14532d',
    journeyTitle: 'New Investor Launchpad',
    tiers: [
      {
        tier: 'free',
        label: 'Start Here — Free',
        subtitle: 'Build your foundation safely',
        tools: [
          { id: 'education',          why: 'Learn what stocks, ETFs, and markets actually are' },
          { id: 'market',             why: 'Understand what moves markets before you invest' },
          { id: 'company',            why: 'Research any company with AI-powered explanations' },
          { id: 'paper-trading',      why: 'Practice with $100K virtual — no real risk' },
          { id: 'watchlist',          why: 'Build a shortlist of stocks to follow' },
          { id: 'portfolio',          why: 'Track your first real holdings as you start' },
          { id: 'technical-analysis', why: 'Understand price charts and basic signals' },
          { id: 'ipo-calendar',       why: 'Learn how new companies enter the market' },
          { id: 'trading',            why: 'Broker-dealer partnership in progress' },
        ],
      },
      {
        tier: 'pro',
        label: 'Pro — $9.99/mo',
        subtitle: 'Level up your research quality',
        tools: [
          { id: 'etf-xray',       why: 'See what\'s inside index funds you already own' },
          { id: 'etf-compare',    why: 'Choose the right ETF for your goals' },
          { id: 'dca-calculator', why: 'Simulate long-term returns from monthly investing' },
          { id: 'recession',      why: 'Understand economic cycles & when to be cautious' },
          { id: 'crypto',         why: 'Research Bitcoin & crypto if you\'re curious' },
        ],
      },
      {
        tier: 'premium',
        label: 'Premium — $19.99/mo',
        subtitle: 'Invest with institutional-quality signals',
        tools: [
          { id: 'rebalancer',       why: 'Keep your portfolio aligned to your target allocation' },
          { id: 'insider-trading',  why: 'See when company insiders are buying their own stock' },
          { id: 'whale-watcher',    why: 'Follow what Buffett & top funds are buying' },
          { id: 'anomaly-detector', why: 'Spot unusual activity before the crowd notices' },
          { id: 'forex',            why: 'Understand currency risk in international holdings' },
        ],
      },
    ],
  },
  {
    id: 'passive',
    label: 'Passive Investor',
    emoji: '📊',
    tagline: 'Smart, steady, long-term growth',
    description: 'Long-term focused. ETFs, DCA, portfolio health. You want compounding to do the work — not daily trading.',
    accentColor: '#3b82f6',
    gradientFrom: '#0c1a3d',
    gradientTo: '#1e3a8a',
    journeyTitle: 'Passive Investor Workflow',
    tiers: [
      {
        tier: 'free',
        label: 'Start Here — Free',
        subtitle: 'Core tools for every passive investor',
        tools: [
          { id: 'market',        why: 'Weekly macro check — not daily noise' },
          { id: 'portfolio',     why: 'Track real holdings and overall performance' },
          { id: 'compare',       why: 'Compare stocks before adding to your portfolio' },
          { id: 'paper-trading', why: 'Test new allocation ideas before committing cash' },
          { id: 'watchlist',     why: 'Monitor ETFs and stocks you are considering' },
          { id: 'ipo-calendar',  why: 'Stay aware of major new index additions' },
          { id: 'trading',       why: 'Broker-dealer partnership in progress' },
        ],
      },
      {
        tier: 'pro',
        label: 'Pro — $9.99/mo',
        subtitle: 'ETF intelligence & long-term modeling',
        tools: [
          { id: 'etf-xray',       why: 'Avoid hidden overlap between ETFs you hold' },
          { id: 'etf-compare',    why: 'Pick the best ETF for each allocation slice' },
          { id: 'dca-calculator', why: 'Model how monthly contributions compound over time' },
          { id: 'recession',      why: 'Know when to shift defensive vs growth allocation' },
          { id: 'crypto',         why: 'Add a crypto sleeve with proper research' },
        ],
      },
      {
        tier: 'premium',
        label: 'Premium — $19.99/mo',
        subtitle: 'Optimize & protect your portfolio',
        tools: [
          { id: 'rebalancer',         why: 'Auto-suggest rebalancing when allocation drifts' },
          { id: 'forex',              why: 'Understand currency exposure in international ETFs' },
          { id: 'whale-watcher',      why: 'See if smart money is rotating out of your holdings' },
          { id: 'crypto-correlation', why: 'Analyze how crypto affects overall portfolio risk' },
          { id: 'anomaly-detector',   why: 'Get alerted to unusual activity in your positions' },
        ],
      },
    ],
  },
  {
    id: 'active',
    label: 'Active Trader',
    emoji: '⚡',
    tagline: 'Edge through data and smart money',
    description: 'Regularly trading stocks. Need real-time signals, technical setups, and smart money intelligence to find an edge.',
    accentColor: '#f59e0b',
    gradientFrom: '#2d1a00',
    gradientTo: '#451a03',
    journeyTitle: 'Active Trader Daily Flow',
    tiers: [
      {
        tier: 'free',
        label: 'Start Here — Free',
        subtitle: 'Pre-market prep and stock research',
        tools: [
          { id: 'market',             why: 'Daily pre-market macro & index check' },
          { id: 'technical-analysis', why: '50+ indicators: RSI, MACD, Bollinger, VWAP' },
          { id: 'company',            why: 'Deep dive on any stock you are eyeing' },
          { id: 'compare',            why: 'Compare sector peers before picking your play' },
          { id: 'paper-trading',      why: 'Backtest new strategies before going live' },
          { id: 'watchlist',          why: 'Your active trade watchlist, always ready' },
          { id: 'ipo-calendar',       why: 'Trade IPO lock-up expirations and momentum' },
          { id: 'trading',            why: 'Broker-dealer partnership in progress' },
        ],
      },
      {
        tier: 'pro',
        label: 'Pro — $9.99/mo',
        subtitle: 'Macro awareness & crypto plays',
        tools: [
          { id: 'recession',  why: 'Position for risk-on vs risk-off regimes correctly' },
          { id: 'crypto',     why: 'Trade BTC/ETH with full on-chain context' },
          { id: 'etf-xray',   why: 'Trade sector ETFs with full holdings visibility' },
          { id: 'dca-calculator', why: 'Size into positions with DCA modeling' },
        ],
      },
      {
        tier: 'premium',
        label: 'Premium — $19.99/mo',
        subtitle: 'Smart money signals & advanced edge',
        tools: [
          { id: 'insider-trading',    why: 'Front-run insider accumulation patterns' },
          { id: 'congress-trades',    why: 'Political trading signals with proven alpha' },
          { id: 'whale-watcher',      why: 'Track Buffett, Dalio, Ackman 13F changes' },
          { id: 'anomaly-detector',   why: 'Get alerted to unusual volume before it moves' },
          { id: 'forex',              why: 'Currency signals that affect your equity positions' },
          { id: 'crypto-correlation', why: 'Use BTC as a macro risk signal for equities' },
          { id: 'rebalancer',         why: 'Keep portfolio risk balanced across positions' },
        ],
      },
    ],
  },
  {
    id: 'options',
    label: 'Options Trader',
    emoji: '🎯',
    tagline: 'Volatility is your edge',
    description: 'Options-focused. You think in Greeks, IV rank, and gamma levels. You need the complete professional toolkit.',
    accentColor: '#a855f7',
    gradientFrom: '#1a0536',
    gradientTo: '#3b0764',
    journeyTitle: 'Options Trader Flow',
    tiers: [
      {
        tier: 'free',
        label: 'Start Here — Free',
        subtitle: 'Research the underlying before trading',
        tools: [
          { id: 'market',             why: 'VIX environment & macro before any position' },
          { id: 'company',            why: 'Earnings dates, fundamentals & catalyst calendar' },
          { id: 'technical-analysis', why: 'Support/resistance levels for strike selection' },
          { id: 'paper-trading',      why: 'Paper trade options strategies before real money' },
          { id: 'ipo-calendar',       why: 'New listings create fresh options opportunities' },
          { id: 'watchlist',          why: 'Track underlyings with upcoming catalysts' },
          { id: 'trading',            why: 'Live execution when your thesis is ready (coming soon)' },
        ],
      },
      {
        tier: 'pro',
        label: 'Pro — $9.99/mo',
        subtitle: 'Macro + crypto options context',
        tools: [
          { id: 'recession',  why: 'Regime awareness for macro directional plays' },
          { id: 'crypto',     why: 'BTC options context alongside equity vol' },
          { id: 'etf-xray',   why: 'Component risk visibility for index spread trades' },
        ],
      },
      {
        tier: 'options_pro',
        label: 'Options Pro — $80/mo',
        subtitle: 'The complete professional options suite',
        tools: [
          { id: 'options',            why: 'Full chain: all strikes, expirations, Greeks, IV' },
          { id: 'iv-crush',           why: 'Predict post-earnings IV collapse — your core edge' },
          { id: 'gamma-exposure',     why: 'Know where market makers MUST hedge — key levels' },
          { id: 'volatility-surface', why: '3D IV surface reveals skew and term structure' },
          { id: 'opex-calendar',      why: 'Trade around expiration with GEX forecasts' },
          { id: 'gex-history',        why: 'See historical GEX patterns that repeat' },
          { id: 'gex-alerts',         why: 'Real-time alerts when gamma levels are breached' },
        ],
      },
      {
        tier: 'premium',
        label: 'Premium — $19.99/mo',
        subtitle: 'Add smart money flow to your thesis',
        tools: [
          { id: 'anomaly-detector', why: 'Unusual options volume often precedes big moves' },
          { id: 'insider-trading',  why: 'Insider buying gives directional conviction' },
          { id: 'whale-watcher',    why: 'Institutional positioning informs your bias' },
          { id: 'forex',            why: 'FX vol correlates with equity vol in multi-asset' },
        ],
      },
    ],
  },
  {
    id: 'sports',
    label: 'Sports Investor',
    emoji: '🏆',
    tagline: 'Where sports capital meets financial markets',
    description: 'Invest at the intersection of sports and capital markets. Public equities, private franchise deals, and shadow token intelligence.',
    accentColor: '#00d4aa',
    gradientFrom: '#001a14',
    gradientTo: '#003d2e',
    journeyTitle: 'Sports Investor Flow',
    tiers: [
      {
        tier: 'free',
        label: 'Start Here — Free',
        subtitle: 'Enter the sports capital markets',
        tools: [
          { id: 'sports-market',     why: 'Trade MANU, DKNG, TKO, NKE & 16 live securities' },
          { id: 'company',           why: 'Deep financial analysis of sports companies' },
          { id: 'technical-analysis',why: 'Chart patterns on sports equities' },
          { id: 'ipo-calendar',      why: 'Upcoming sports-adjacent IPOs and listings' },
          { id: 'watchlist',         why: 'Track your sports equity universe' },
          { id: 'paper-trading',     why: 'Practice sports equity trading risk-free' },
          { id: 'sports-org-portal', why: 'Apply for franchise Tier B certification' },
          { id: 'trading',           why: 'Broker-dealer partnership in progress' },
        ],
      },
      {
        tier: 'pro',
        label: 'Pro — $9.99/mo',
        subtitle: 'Broader market context for sports plays',
        tools: [
          { id: 'etf-xray',  why: 'Understand index weight of your sports holdings' },
          { id: 'recession', why: 'Consumer spending cycles affect sports revenues directly' },
          { id: 'crypto',    why: 'Fan tokens & crypto intersect with sports capital' },
        ],
      },
      {
        tier: 'premium',
        label: 'Premium — $19.99/mo',
        subtitle: 'Private market intelligence & smart money',
        tools: [
          { id: 'sports-private',  why: 'Tier A/B/C intelligence, shadow tokens, deal rooms' },
          { id: 'whale-watcher',   why: 'Track institutional sports investment patterns' },
          { id: 'insider-trading', why: 'Insider signals on public sports companies' },
          { id: 'anomaly-detector',why: 'Unusual volume before sports news breaks' },
          { id: 'forex',           why: 'Currency exposure for international club investments' },
        ],
      },
    ],
  },
];

const STORAGE_KEY = 'iq_persona';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function btnStyle(bg, color, border) {
  return {
    padding: '0.4rem 0.875rem', background: bg,
    border: `1px solid ${border}`, borderRadius: '0.5rem',
    color, cursor: 'pointer', fontSize: '0.775rem', fontWeight: '600',
    display: 'inline-flex', alignItems: 'center', transition: 'all 0.18s',
  };
}


// ─── Top Winners Strip ────────────────────────────────────────────────────────
const API_URL_TW = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';
const SCORE_COLOR_TW = s => s >= 85 ? '#10b981' : s >= 70 ? '#f59e0b' : '#ef4444';

const TopWinnersStrip = ({ setActiveTab }) => {
  React.useEffect(() => {
    const style = document.getElementById('tw-strip-style');
    if (!style) {
      const s = document.createElement('style');
      s.id = 'tw-strip-style';
      s.textContent = '.tw-scroll::-webkit-scrollbar { display: none; }';
      document.head.appendChild(s);
    }
  }, []);
  const [picks, setPicks]     = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetch(`${API_URL_TW}/top-winners`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.picks?.length) setPicks(data.picks.slice(0, 6)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '12px', padding: '0.7rem 1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
      <Trophy size={13} color="#818cf8" />
      <span style={{ color: '#64748b', fontSize: '0.72rem' }}>Loading AI top picks...</span>
    </div>
  );

  if (!picks.length) return null;

  return (
    <div style={{ background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '12px', padding: '0.65rem 1rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.55rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
          <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Trophy size={12} color="#818cf8" />
          </div>
          <span style={{ color: '#f1f5f9', fontSize: '0.85rem', fontWeight: 700 }}>AI Top Picks</span>
          <span style={{ color: '#475569', fontSize: '0.72rem' }}>· 50 anchor + live discovery · 6 signals · editorial research only</span>
        </div>
        <button onClick={() => setActiveTab('top-winners')} style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '6px', padding: '0.22rem 0.65rem', color: '#818cf8', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
          View all 10 →
        </button>
      </div>

      <div className="tw-scroll" style={{ display: 'flex', gap: '0.45rem', overflowX: 'auto', paddingBottom: '0.15rem', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {picks.map((p, i) => {
          const sc = SCORE_COLOR_TW(p.score);
          return (
            <button key={p.symbol} onClick={() => setActiveTab('top-winners')}
              style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.45rem', background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: '8px', padding: '0.4rem 0.65rem', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#1e293b'}
            >
              <span style={{ fontSize: '0.58rem', color: '#334155', fontWeight: 700 }}>#{i+1}</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#f1f5f9', lineHeight: 1 }}>{p.symbol}</div>
                <div style={{ fontSize: '0.6rem', color: '#475569', marginTop: '0.1rem', maxWidth: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(p.name || '').split(' ').slice(0,2).join(' ')}</div>
              </div>
              {p.upsidePct && (
                <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#34d399', background: 'rgba(16,185,129,0.1)', borderRadius: '4px', padding: '0.08rem 0.3rem' }}>{p.upsidePct}</span>
              )}
              <div style={{ width: 26, height: 26, borderRadius: '50%', border: `2px solid ${sc}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '0.56rem', fontWeight: 700, color: sc, fontFamily: 'monospace' }}>{p.score}</span>
              </div>
            </button>
          );
        })}
      </div>

      <p style={{ color: '#64748b', fontSize: '0.62rem', margin: '0.45rem 0 0', lineHeight: 1.4 }}>
        ⚠ <strong style={{ color: '#f87171' }}>Not investment advice</strong> · Editorial &amp; educational research only · InvestorQ is not an RIA · Always consult a qualified financial advisor before investing
      </p>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const HomeDashboard = ({ setActiveTab, setSelectedStock, user, marketData, portfolioSummary, setShowAuth }) => {
  const [selectedPersona, setSelectedPersona] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [expandedTier, setExpandedTier] = useState(0);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => { setTimeout(() => setAnimateIn(true), 40); }, []);

  const persona = PERSONAS.find(p => p.id === selectedPersona);

  const handleSelectPersona = (id) => {
    // Toggle off if clicking the already-selected persona
    const next = selectedPersona === id ? null : id;
    setSelectedPersona(next);
    setExpandedTier(0);
    try {
      if (next) localStorage.setItem(STORAGE_KEY, next);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  const fade = {
    opacity: animateIn ? 1 : 0,
    transform: animateIn ? 'translateY(0)' : 'translateY(14px)',
    transition: 'opacity 0.45s ease, transform 0.45s ease',
  };

  // Main Dashboard — picker is always inline at top
  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <RunningTicker />

      {/* ── Market Movers strip — compact 3 blocks right under ticker ── */}
      <MarketMovers setActiveTab={setActiveTab} onSelectStock={(sym) => { if (sym && setSelectedStock) { setSelectedStock(sym); setActiveTab('company'); } }} />
      <p style={{ fontSize: '0.65rem', color: '#64748b', margin: '-0.4rem 0 0.5rem', lineHeight: 1.5 }}>
        Movers sourced from NYSE · NASDAQ · AMEX — broader universe than large-cap only screeners. Min $10 price · 5M+ daily volume.
      </p>

      {/* ── AI Top Winners strip ── */}
      <TopWinnersStrip setActiveTab={setActiveTab} />

      <div style={fade}>
        <InlinePersonaSelector
          personas={PERSONAS}
          selectedPersona={selectedPersona}
          onSelect={handleSelectPersona}
        />

        <HeroBanner user={user} persona={persona} setActiveTab={setActiveTab} setShowAuth={setShowAuth} />

        {/* ── Persona journey (always shown when persona is set) ── */}
        {persona && (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.625rem', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.05rem', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>{persona.journeyTitle}</h2>
              <span style={{ color: '#475569', fontSize: '0.75rem' }}>Tiered from free to paid — expand each tier to explore</span>
            </div>
            <PersonaJourney persona={persona} setActiveTab={setActiveTab} expandedTier={expandedTier} setExpandedTier={setExpandedTier} />
          </>
        )}
        {!persona && (
          <div style={{ padding: '2rem 1.5rem', background: 'rgba(15,23,42,0.5)', border: '1px solid #1e293b', borderRadius: '1rem', textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '1.375rem', marginBottom: '0.5rem' }}>👆</div>
            <div style={{ fontWeight: '600', color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Pick your investor profile above</div>
            <div style={{ color: '#475569', fontSize: '0.78rem' }}>You'll get a personalised tiered path through all 42 tools</div>
          </div>
        )}

        {/* ── Full capability segments — always visible below ── */}
        <FullCapabilityGrid capabilities={CAPABILITIES} setActiveTab={setActiveTab} />

        {!user && <GuestCTA setShowAuth={setShowAuth} />}

        <div style={{ marginTop: '1.75rem', padding: '0.875rem 1rem', background: 'rgba(239,68,68,0.07)', borderRadius: '0.5rem', border: '1px solid rgba(239,68,68,0.15)' }}>
          <p style={{ color: '#fca5a5', fontSize: '0.7rem', margin: 0, lineHeight: 1.5 }}>
            <strong>Educational Purpose:</strong> InvestorQ provides financial data and analysis tools for educational and informational purposes only. This is not financial advice. Always conduct your own research and consult a qualified financial advisor before making investment decisions.
          </p>
        </div>
      </div>
      <PageAwareAI pageId="home" pageData={{ marketSummary: marketData, portfolioSummary }} />
    </div>
  );
};

// ─── Inline Persona Selector — always visible at top of page ─────────────────
const InlinePersonaSelector = ({ personas, selectedPersona, onSelect }) => (
  <div style={{
    background: 'rgba(15,23,42,0.8)',
    border: '1px solid rgba(99,102,241,0.25)',
    borderRadius: '1rem',
    padding: 'clamp(0.875rem, 3vw, 1.25rem) clamp(1rem, 3vw, 1.5rem)',
    marginBottom: '1.25rem',
  }}>
    {/* Header row */}
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.875rem', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flex: 1, minWidth: 0 }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '0.5rem', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Sparkles size={15} color="#818cf8" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 'clamp(0.825rem, 2.5vw, 0.9rem)', fontWeight: '700', color: '#f1f5f9', lineHeight: 1.2 }}>Your Experience & Journey</div>
          <div style={{ fontSize: 'clamp(0.65rem, 2vw, 0.72rem)', color: '#64748b', marginTop: '0.15rem', lineHeight: 1.4 }}>
            {selectedPersona
              ? 'Path active below — tap any profile to switch'
              : 'Pick your investor profile for a personalised path through all 42 tools'}
          </div>
        </div>
      </div>
      {selectedPersona && (
        <span style={{ fontSize: '0.68rem', color: '#22c55e', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: '1rem', padding: '0.2rem 0.7rem', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}>
          ● Path active
        </span>
      )}
      {!selectedPersona && (
        <span style={{ fontSize: '0.68rem', color: '#f59e0b', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '1rem', padding: '0.2rem 0.7rem', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}>
          ○ No profile selected
        </span>
      )}
    </div>

    {/* Persona grid — equal columns, fills full width, wraps cleanly */}
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '0.5rem',
    }}
      className="persona-pill-grid"
    >
      {personas.map(p => {
        const isSelected = selectedPersona === p.id;
        return (
          <button
            key={p.id}
            onClick={() => onSelect(p.id)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
              padding: '0.5rem 0.625rem',
              background: isSelected
                ? `linear-gradient(135deg, ${p.gradientFrom} 0%, ${p.gradientTo} 100%)`
                : 'rgba(30,41,59,0.7)',
              border: `1px solid ${isSelected ? p.accentColor + '80' : '#334155'}`,
              borderRadius: '0.625rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: isSelected ? `0 4px 18px ${p.accentColor}28` : 'none',
              WebkitTapHighlightColor: 'transparent',
              width: '100%',
            }}
          >
            <span style={{ fontSize: '0.95rem', lineHeight: 1, flexShrink: 0 }}>{p.emoji}</span>
            <span style={{
              fontSize: 'clamp(0.68rem, 1.8vw, 0.8rem)',
              fontWeight: isSelected ? '700' : '500',
              color: isSelected ? '#f1f5f9' : '#94a3b8',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}>{p.label}</span>
            {isSelected && (
              <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: p.accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Check size={8} color="black" strokeWidth={3} />
              </div>
            )}
          </button>
        );
      })}
      <style>{`
        @media (max-width: 480px) {
          .persona-pill-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </div>
  </div>
);

// ─── Persona Card ─────────────────────────────────────────────────────────────
const PersonaCard = ({ persona, isSelected, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const active = hovered || isSelected;
  const totalTools = persona.tiers.reduce((sum, t) => sum + t.tools.length, 0);
  const freeCount = persona.tiers.find(t => t.tier === 'free')?.tools.length || 0;

  return (
    <button onClick={() => onSelect(persona.id)}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        background: active ? `linear-gradient(135deg, ${persona.gradientFrom} 0%, ${persona.gradientTo} 100%)` : 'rgba(15,23,42,0.8)',
        border: `1px solid ${active ? persona.accentColor + '55' : '#1e293b'}`,
        borderRadius: '1rem', padding: '1.375rem', cursor: 'pointer', textAlign: 'left',
        transition: 'all 0.22s', transform: active ? 'translateY(-3px)' : 'none',
        boxShadow: active ? `0 8px 28px ${persona.accentColor}18` : 'none',
        position: 'relative', overflow: 'hidden', width: '100%',
      }}>
      {isSelected && (
        <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', width: '20px', height: '20px', background: persona.accentColor, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Check size={11} color="black" strokeWidth={3} />
        </div>
      )}
      <div style={{ fontSize: '1.875rem', marginBottom: '0.625rem' }}>{persona.emoji}</div>
      <div style={{ fontWeight: '700', fontSize: '1rem', color: '#f1f5f9', marginBottom: '0.2rem' }}>{persona.label}</div>
      <div style={{ fontSize: '0.7rem', color: persona.accentColor, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.055em', marginBottom: '0.55rem' }}>{persona.tagline}</div>
      <p style={{ color: '#94a3b8', fontSize: '0.78rem', lineHeight: 1.5, margin: '0 0 0.875rem' }}>{persona.description}</p>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.67rem', color: '#60a5fa', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '1rem', padding: '0.15rem 0.55rem' }}>
          🆓 {freeCount} free tools
        </span>
        <span style={{ fontSize: '0.67rem', color: '#64748b', background: 'rgba(255,255,255,0.04)', border: '1px solid #1e293b', borderRadius: '1rem', padding: '0.15rem 0.55rem' }}>
          {totalTools} total
        </span>
      </div>
    </button>
  );
};

// ─── Hero Banner ─────────────────────────────────────────────────────────────
const HeroBanner = ({ user, persona, setActiveTab, setShowAuth }) => {
  const accent = persona?.accentColor || '#818cf8';
  const bg = persona
    ? `linear-gradient(135deg, ${persona.gradientFrom} 0%, ${persona.gradientTo} 55%, #0f172a 100%)`
    : 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)';

  return (
    <div style={{ background: bg, borderRadius: '1rem', padding: '1.875rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden', border: `1px solid ${accent}18`, boxShadow: `0 0 50px ${accent}0d` }}>
      <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: `radial-gradient(circle, ${accent}12 0%, transparent 70%)`, borderRadius: '50%' }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Sparkles size={13} color={accent} />
            <span style={{ color: accent, fontSize: 'clamp(0.65rem, 2vw, 0.75rem)', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {persona ? `${persona.emoji} ${persona.label} Mode` : 'Welcome to InvestorQ'}
            </span>
          </div>
        </div>

        <h1 style={{ fontSize: 'clamp(1.2rem, 4vw, 2rem)', fontWeight: '800', color: '#f8fafc', margin: '0 0 0.5rem', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
          {user
            ? `${persona ? `${persona.emoji} ` : ''}Hey ${user.email?.split('@')[0] || 'Investor'} — ${persona?.tagline || 'Ready to research?'}`
            : persona ? `${persona.emoji} ${persona.label} Dashboard` : 'Professional Financial Analysis'
          }
        </h1>

        <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', maxWidth: '540px', lineHeight: 1.6, margin: '0 0 1rem' }}>
          {persona ? persona.description : 'Your all-in-one platform for market research, stock analysis, options trading, and portfolio management.'}
        </p>

        {persona && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {persona.tiers[0]?.tools.slice(0, 3).map(t => {
              const cap = CAPABILITIES[t.id];
              if (!cap) return null;
              const Icon = cap.icon;
              return (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  style={{
                    padding: 'clamp(0.35rem, 1.5vw, 0.45rem) clamp(0.625rem, 2vw, 0.875rem)',
                    background: `${accent}18`, border: `1px solid ${accent}38`,
                    borderRadius: '0.5rem', color: 'white', cursor: 'pointer',
                    fontSize: 'clamp(0.68rem, 2vw, 0.775rem)', fontWeight: '600',
                    display: 'flex', alignItems: 'center', gap: '0.3rem',
                    transition: 'all 0.18s', WebkitTapHighlightColor: 'transparent',
                  }}>
                  <Icon size={11} color={accent} /> {cap.title}
                </button>
              );
            })}
          </div>
        )}

        {!user && !persona && (
          <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap' }}>
            <button onClick={() => setShowAuth && setShowAuth(true)} style={{ padding: 'clamp(0.5rem, 2vw, 0.6rem) clamp(1rem, 3vw, 1.25rem)', background: accent, border: 'none', borderRadius: '0.5rem', color: 'black', cursor: 'pointer', fontSize: 'clamp(0.8rem, 2.5vw, 0.875rem)', fontWeight: '700' }}>Get Started Free</button>
            <button onClick={() => setActiveTab('market')} style={{ padding: 'clamp(0.5rem, 2vw, 0.6rem) clamp(1rem, 3vw, 1.25rem)', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: '0.5rem', color: 'white', cursor: 'pointer', fontSize: 'clamp(0.8rem, 2.5vw, 0.875rem)', fontWeight: '600' }}>Explore Markets →</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Persona Journey ──────────────────────────────────────────────────────────
const PersonaJourney = ({ persona, setActiveTab, expandedTier, setExpandedTier }) => (
  <div style={{ marginBottom: '2.5rem' }}>
    {persona.tiers.map((tier, idx) => (
      <TierSection key={tier.tier} tier={tier} isExpanded={expandedTier === idx}
        onToggle={() => setExpandedTier(expandedTier === idx ? -1 : idx)}
        setActiveTab={setActiveTab} isFirst={idx === 0} />
    ))}
  </div>
);

// ─── Tier Section ─────────────────────────────────────────────────────────────
const TierSection = ({ tier, isExpanded, onToggle, setActiveTab, isFirst }) => {
  const t = TIERS[tier.tier];
  return (
    <div style={{ marginBottom: '0.75rem', border: `1px solid ${isExpanded ? t.border : '#1e293b'}`, borderRadius: '0.875rem', overflow: 'hidden', transition: 'border-color 0.2s' }}>
      <button onClick={onToggle} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.875rem', padding: '1rem 1.25rem', background: isExpanded ? t.bg : 'rgba(15,23,42,0.6)', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '0.625rem', background: t.bg, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
          {t.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: '700', fontSize: '0.95rem', color: '#f1f5f9' }}>{tier.label}</span>
            {isFirst && <span style={{ fontSize: '0.62rem', fontWeight: '700', color: '#22c55e', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: '1rem', padding: '0.1rem 0.5rem' }}>START HERE</span>}
          </div>
          <div style={{ color: '#64748b', fontSize: '0.77rem', marginTop: '0.1rem' }}>{tier.subtitle} · {tier.tools.length} tools</div>
        </div>
        <ChevronRight size={16} color="#475569" style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
      </button>
      {isExpanded && (
        <div style={{ padding: '0 1.125rem 1.125rem', background: 'rgba(10,15,30,0.4)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(245px, 100%), 1fr))', gap: '0.625rem', paddingTop: '0.875rem' }}>
            {tier.tools.map(tool => {
              const cap = CAPABILITIES[tool.id];
              if (!cap) return null;
              return <ToolCard key={tool.id} cap={cap} why={tool.why} tier={t} setActiveTab={setActiveTab} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tool Card ────────────────────────────────────────────────────────────────
const ToolCard = ({ cap, why, tier, setActiveTab }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = cap.icon;
  return (
    <button onClick={() => setActiveTab(cap.id)} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.875rem', background: hovered ? `${tier.color}0e` : 'rgba(15,23,42,0.7)', border: `1px solid ${hovered ? tier.border : '#1e293b'}`, borderRadius: '0.625rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.18s', transform: hovered ? 'translateY(-1px)' : 'none', width: '100%' }}>
      <div style={{ width: '34px', height: '34px', borderRadius: '0.5rem', background: `${tier.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={17} color={tier.color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: '600', fontSize: '0.825rem', color: '#f1f5f9', display: 'block', marginBottom: '0.2rem' }}>{cap.title}</span>
        <p style={{ color: '#475569', fontSize: '0.71rem', margin: '0 0 0.3rem', lineHeight: 1.4 }}>{cap.desc}</p>
        {why && <p style={{ color: tier.color, fontSize: '0.68rem', margin: 0, lineHeight: 1.4, opacity: 0.85 }}>→ {why}</p>}
      </div>
    </button>
  );
};

// ─── Full Capability Grid — always visible below persona journey ──────────────
const CATEGORY_SEGMENTS = [
  {
    title: 'Market Analysis',
    subtitle: 'Real-time data, macro indicators & economic signals',
    color: '#3b82f6',
    ids: ['market', 'recession', 'ipo-calendar', 'congress-trades', 'whale-watcher', 'forex'],
  },
  {
    title: 'Stock Research',
    subtitle: 'Deep-dive analysis, comparison & smart money tracking',
    color: '#22c55e',
    ids: ['company', 'technical-analysis', 'insider-trading', 'compare', 'anomaly-detector', 'top-winners'],
  },
  {
    title: 'Sports Markets',
    subtitle: 'Public equities, private deals & franchise intelligence',
    color: '#00d4aa',
    ids: ['sports-market', 'sports-private', 'sports-org-portal'],
  },
  {
    title: 'Options Trading',
    subtitle: 'Advanced options analysis, Greeks & volatility tools',
    color: '#a855f7',
    ids: ['options', 'gamma-exposure', 'volatility-surface', 'opex-calendar', 'gex-history', 'gex-alerts', 'iv-crush'],
  },
  {
    title: 'ETF Intelligence',
    subtitle: 'Holdings breakdown, overlap detection & comparisons',
    color: '#f59e0b',
    ids: ['etf-xray', 'etf-compare'],
  },
  {
    title: 'Cryptocurrency',
    subtitle: 'Digital asset analysis, tracking & correlation',
    color: '#f97316',
    ids: ['crypto', 'crypto-correlation'],
  },
  {
    title: 'Portfolio Management',
    subtitle: 'Track, optimize and grow your investments',
    color: '#ec4899',
    ids: ['portfolio', 'rebalancer', 'paper-trading', 'dca-calculator', 'watchlist', 'trading'],
  },
  {
    title: 'Learn & Configure',
    subtitle: 'Education, structured courses & account settings',
    color: '#06b6d4',
    ids: ['education', 'learn', 'settings'],
  },
];

const FullCapabilityGrid = ({ capabilities, setActiveTab }) => (
  <div>
    {/* Section divider */}
    <div style={{ margin: '2.5rem 0 2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
        <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to right, #334155, transparent)' }} />
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.625rem',
          background: 'rgba(30,41,59,0.8)',
          border: '1px solid #334155',
          borderRadius: '2rem',
          padding: '0.5rem 1.25rem',
        }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />
          <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#cbd5e1', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            All Capabilities
          </span>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#a855f7', flexShrink: 0 }} />
        </div>
        <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to left, #334155, transparent)' }} />
      </div>
      <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#475569', margin: 0 }}>
        Browse every tool across all categories — click any card to open it
      </p>
    </div>

    {CATEGORY_SEGMENTS.map(seg => {
      const tools = seg.ids.map(id => capabilities[id]).filter(Boolean);
      const count = tools.length;

      // Pick columns so tiles fill rows evenly — no orphans, no huge gaps
      // Logic: find the largest divisor of count that gives cols <= 4
      const cols = count <= 2 ? count
                 : count === 3 ? 3
                 : count === 4 ? 4
                 : count === 5 ? 5
                 : count === 6 ? 3  // 2 rows of 3
                 : count === 7 ? 4  // 2 rows: 4+3 — best fit
                 : 4;               // 8+ → 4 cols

      return (
        <div key={seg.title} style={{ marginBottom: '2rem' }}>
          {/* Category header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.875rem' }}>
            <div style={{ width: '4px', height: '28px', background: seg.color, borderRadius: '2px', flexShrink: 0 }} />
            <div>
              <h2 style={{ fontSize: '1.05rem', fontWeight: '700', color: '#f1f5f9', margin: 0, lineHeight: 1.2 }}>{seg.title}</h2>
              <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0, marginTop: '0.15rem' }}>{seg.subtitle}</p>
            </div>
            <span style={{ marginLeft: 'auto', fontSize: '0.68rem', color: seg.color, background: `${seg.color}12`, border: `1px solid ${seg.color}30`, borderRadius: '1rem', padding: '0.2rem 0.625rem', flexShrink: 0 }}>
              {tools.length} tools
            </span>
          </div>

          {/* Tile grid — fixed columns so rows are always full */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: '0.75rem',
          }}
            className={`cap-grid cap-grid-${cols}`}
          >
            {tools.map(cap => {
              const tier = TIERS[cap.tier];
              return <CapabilityTile key={cap.id} cap={cap} tier={tier} segColor={seg.color} setActiveTab={setActiveTab} />;
            })}
          </div>

          {/* Responsive: collapse to 2 cols on tablet, 1 on mobile */}
          <style>{`
            @media (max-width: 900px) { .cap-grid { grid-template-columns: repeat(2, 1fr) !important; } }
            @media (max-width: 500px) { .cap-grid { grid-template-columns: 1fr !important; } }
          `}</style>
        </div>
      );
    })}
  </div>
);

// ─── Capability Tile — richer than ToolCard, used in the always-on grid ───────
const CapabilityTile = ({ cap, tier, segColor, setActiveTab }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = cap.icon;
  return (
    <button
      onClick={() => setActiveTab(cap.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '1rem',
        padding: '1.125rem 1.25rem',
        background: hovered
          ? `linear-gradient(135deg, ${segColor}10 0%, rgba(15,23,42,0.85) 100%)`
          : 'rgba(15,23,42,0.7)',
        border: `1px solid ${hovered ? segColor + '55' : '#1e293b'}`,
        borderRadius: '0.875rem',
        cursor: 'pointer', textAlign: 'left', width: '100%',
        transition: 'all 0.2s',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? `0 6px 24px ${segColor}12` : 'none',
      }}
    >
      {/* Icon block */}
      <div style={{
        width: '44px', height: '44px', borderRadius: '0.75rem', flexShrink: 0,
        background: hovered ? `${segColor}22` : `${segColor}14`,
        border: `1px solid ${hovered ? segColor + '40' : segColor + '20'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.2s',
      }}>
        <Icon size={21} color={segColor} />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#f1f5f9' }}>{cap.title}</span>
          <span style={{
            fontSize: '0.58rem', fontWeight: '700', padding: '0.1rem 0.45rem',
            background: tier.bg, border: `1px solid ${tier.border}`,
            borderRadius: '1rem', color: tier.color, textTransform: 'uppercase',
            letterSpacing: '0.04em', flexShrink: 0,
          }}>{tier.icon} {tier.label}</span>
        </div>
        <p style={{ color: '#64748b', fontSize: '0.775rem', margin: 0, lineHeight: 1.45 }}>{cap.desc}</p>
      </div>

      <ArrowRight size={15} color={hovered ? segColor : '#334155'} style={{ flexShrink: 0, transition: 'color 0.2s' }} />
    </button>
  );
};

// ─── Guest CTA ────────────────────────────────────────────────────────────────
const GuestCTA = ({ setShowAuth }) => (
  <div style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.07) 0%, rgba(139,92,246,0.07) 100%)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '1rem', padding: '1.75rem', textAlign: 'center', margin: '2rem 0 1.5rem' }}>
    <div style={{ fontSize: '1.375rem', marginBottom: '0.625rem' }}>🔓</div>
    <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#f1f5f9', marginBottom: '0.45rem' }}>Unlock Your Full Investment Edge</h3>
    <p style={{ color: '#64748b', fontSize: '0.845rem', lineHeight: 1.6, maxWidth: '460px', margin: '0 auto 1.25rem' }}>
      Free account gives you portfolio tracking, watchlists, paper trading, AI analysis, and your personalized path through all 42 tools.
    </p>
    <button onClick={() => setShowAuth && setShowAuth(true)}
      style={{ padding: '0.8rem 2rem', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '700', boxShadow: '0 4px 18px rgba(99,102,241,0.35)' }}>
      Create Free Account →
    </button>
  </div>
);

export default HomeDashboard;